import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Building2 } from "lucide-react";
import { Logo } from "../components/Logo";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { api, errorMessage } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { LanguageToggle } from "../components/LanguageToggle";

export default function RegisterPage() {
  const { register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orgNumber, setOrgNumber] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState(searchParams.get("epost") || "");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lookup, setLookup] = useState({ status: "idle", name: "", message: "" });

  const digits = orgNumber.replace(/\D/g, "");

  useEffect(() => {
    if (digits.length !== 9) {
      setLookup({ status: "idle", name: "", message: "" });
      return;
    }
    let active = true;
    setLookup({ status: "loading", name: "", message: "" });
    const timer = setTimeout(() => {
      api
        .get(`/brreg/${digits}`)
        .then((res) => {
          if (active) setLookup({ status: "found", name: res.data.name, message: "" });
        })
        .catch((err) => {
          if (active)
            setLookup({
              status: "notfound",
              name: "",
              message: errorMessage(err, t("Fant ikke organisasjonsnummeret")),
            });
        });
    }, 400);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [digits]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lookup.status !== "found") {
      toast.error(t("Oppgi et gyldig organisasjonsnummer (9 siffer)"));
      return;
    }
    if (password.length < 8) {
      toast.error(t("Passordet må være minst 8 tegn"));
      return;
    }
    const phoneDigits = phone.replace(/[\s.\-()]/g, "").replace(/^(\+47|0047)/, "");
    if (phoneDigits && !/^\d{8}$/.test(phoneDigits)) {
      toast.error(t("Telefonnummer må være 8 siffer (eller la feltet stå tomt)"));
      return;
    }
    setSubmitting(true);
    try {
      const data = await register(digits, email.trim(), password, fullName.trim(), phoneDigits);
      if (data.status === "pending") {
        toast.info(t(data.status_message || "Kontoen venter på godkjenning."), { duration: 9000 });
      } else {
        toast.success(t(data.status_message || "Kontoen er opprettet – velkommen!"));
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background hero-decor flex flex-col">
      <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="register-logo-link">
          <Logo showTagline />
        </Link>
        <LanguageToggle />
      </div>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md border-border shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">{t("Bli arrangør")}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {t("Oppgi organisasjonsnummer, så henter vi firmanavnet automatisk fra Brønnøysundregistrene.")}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="reg-orgnr">{t("Organisasjonsnummer")}</Label>
                <Input
                  id="reg-orgnr"
                  inputMode="numeric"
                  value={orgNumber}
                  onChange={(e) => setOrgNumber(e.target.value)}
                  placeholder={t("9 siffer, f.eks. 923 609 016")}
                  required
                  data-testid="register-orgnumber-input"
                />
                {lookup.status === "loading" && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="register-brreg-loading">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {t("Søker i Brønnøysundregistrene...")}
                  </p>
                )}
                {lookup.status === "found" && (
                  <div className="rounded-lg bg-accent text-accent-foreground px-3 py-2 flex items-center gap-2 text-sm" data-testid="register-brreg-found">
                    <CheckCircle2 className="w-4 h-4 shrink-0 text-[hsl(var(--success))]" />
                    <span className="flex items-center gap-1.5 min-w-0">
                      <Building2 className="w-4 h-4 shrink-0" />
                      <span className="font-medium truncate">{lookup.name}</span>
                    </span>
                  </div>
                )}
                {lookup.status === "notfound" && (
                  <p className="text-xs text-[hsl(var(--danger))] flex items-center gap-1.5" data-testid="register-brreg-notfound">
                    <XCircle className="w-3.5 h-3.5" />
                    {lookup.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reg-fullname">{t("Fullt navn")}</Label>
                <Input
                  id="reg-fullname"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t("Fornavn og etternavn")}
                  required
                  data-testid="register-fullname-input"
                />
                <p className="text-xs text-muted-foreground">
                  {t("Sjekkes mot organisasjonens registrerte roller (styre, daglig leder m.m.) i Brønnøysundregistrene.")}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reg-email">{t("E-post")}</Label>
                <Input
                  id="reg-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="din@epost.no"
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reg-phone">
                  {t("Mobilnummer")} <span className="text-muted-foreground font-normal">{t("(valgfritt)")}</span>
                </Label>
                <Input
                  id="reg-phone"
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t("8 siffer")}
                  maxLength={16}
                  data-testid="register-phone-input"
                />
                <p className="text-xs text-muted-foreground">
                  {t("Brukes bare hvis vi må kontakte deg om kontoen – for eksempel hvis bekreftelseskoden ikke kommer frem på e-post.")}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="reg-password">{t("Passord")}</Label>
                <Input
                  id="reg-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("Minst 8 tegn")}
                  required
                  minLength={8}
                  data-testid="register-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11"
                disabled={submitting || lookup.status !== "found"}
                data-testid="register-submit-button"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("Oppretter konto...")}</>
                ) : (
                  t("Opprett konto")
                )}
              </Button>
              <p className="text-xs text-muted-foreground">
                {t("Vi henter kun firmanavnet fra Brønnøysundregistrene – ingen andre data lagres.")}
              </p>
            </form>
            <p className="text-sm text-muted-foreground text-center mt-6">
              {t("Har du allerede konto?")}{" "}
              <Link to="/logg-inn" className="font-medium text-foreground underline underline-offset-4" data-testid="register-login-link">
                {t("Logg inn")}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
